import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormRowView, PageContainer, PageContentView, WidgetContainer, WidgetView } from 'components/shared';
import { PrimaryButton, SecondaryButton } from 'components/ui';
import Classes, { Account, PositivePayCheck } from "model";
import React from 'react';
import TASPositivePayCheckForm from "./classes/TASPositivePayCheckForm";
import TASPositivePayAddChecksDesktopView from "./components/TASPositivePayAddChecksDesktopView";
import TASPositivePayAddChecksMobileView from "./components/TASPositivePayAddChecksMobileView";
import "./styles.css";
import "./mobile.css";

/**
 * @function TASPositivePayAddChecksPage
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay Add Check page.
 */
export default function TASPositivePayAddChecksPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  const data = { ...getDefaultData(undefined, context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const { nextStep } = useWorkflow() ?? {};

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    onDesktopChange: (id, prop) => val => {
      const checkForms = state.data.checkForms;
      const findSelectedIndex = checkForms.findIndex(item => item.id === id);
      const selectedCheckform = checkForms.filter(item => item.id === id)[0];
      const filteredCheckForms = checkForms.filter(item => item.id !== id);
      const currCheckFormRecord = { ...selectedCheckform.record };
      currCheckFormRecord[prop] = val;
      currCheckFormRecord["mobileViewIsEditing"] = true;
      currCheckFormRecord["mobileViewIsOpen"] = false;
      const updatedCheckForm = TASPositivePayCheckForm.init({ ...currCheckFormRecord, account: currCheckFormRecord.account.id });
      
      const updatedCheckForms = [ 
        ...filteredCheckForms.slice(0, findSelectedIndex), 
        updatedCheckForm, 
        ...filteredCheckForms.slice(findSelectedIndex) 
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], updatedCheckForms)
      .submit(setState);
    },

    onMobileSave: (id, stateEditCheckForms) => val => {
      var editCheckForms = stateEditCheckForms;
      var findSelectedIndex = editCheckForms.findIndex(item => item.id === id);
      var selectedEditCheckForm = editCheckForms[findSelectedIndex];
      const currCheckFormRecord = { 
        ...selectedEditCheckForm.record,
         account: selectedEditCheckForm.account.id,
        mobileViewIsEditing: false 
      };     

      const defaultCheck = BCMSDefaultModel.positivePayCheck();
      const defaultCheckRecord = defaultCheck.record;
      const merge = BCMSDefaultModel.mergeRecords(currCheckFormRecord, defaultCheckRecord);

      const updatedEditCheckForm = TASPositivePayCheckForm.init(merge);

      const currCheckForms = state.data.checkForms;
      const filteredCheckForms = currCheckForms.filter(item => item.id !== id);

      const updatedCheckForms = [ 
        ...filteredCheckForms.slice(0, findSelectedIndex), 
        updatedEditCheckForm, 
        ...filteredCheckForms.slice(findSelectedIndex) 
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], updatedCheckForms)
      .submit(setState);
    },

    onAddChecksClick: evt => {
      let checkForms = state.data.checkForms;
      let addedCheckForm = getDefaultData().defaultCheckForm;
      let largestID = checkForms.reduce((acc, item) => {
        let accID = Number(acc);
        let id = Number(item.id);
        if(id > accID) {
          acc = `${id}`;
        }
        return acc;
      }, "0");

      largestID = Number(largestID) + 1;
      addedCheckForm["id"] = `${ largestID }`;

      checkForms = checkForms.reduce((acc, item) => {
        const updatedItem = TASPositivePayCheckForm.init({ ...item.record, mobileViewIsEditing: true, mobileViewIsOpen: false });
        acc.push(updatedItem);
        return acc;
      }, []);

      checkForms = [ 
        ...checkForms,
        addedCheckForm
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], checkForms)
      .submit(setState);
    },

    onDeleteClick: id => evt => {
      const checkForms = state.data.checkForms;
      const filteredCheckForms = checkForms.filter(item => item.id !== id);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], filteredCheckForms)
      .submit(setState);
    },

    onSubmitClick: callback => evt => {
      const checkForms = state.data.checkForms;
      let submittedChecks = [];
      const defaultCheckForm = state.data.defaultCheckForm;

      let createdChecks = checkForms.reduce((acc, item) => {
        const defaultCheck = BCMSDefaultModel.positivePayCheck();
        const defaultCheckRecord = defaultCheck.record;
        let canCreateCheck = false;

        // Check if this check form has been modified by
        // relevant fields noted as "validKey" in the code below.
        Object.keys(defaultCheckForm.record).map(key => {
          const defaultVal = defaultCheckForm.record[key];
          const currVal = item.record[key];
          const validKey = key === "issuedDate" || key === "checkNumber" || key === "amount" || key === "payee"
          || key === "referenceNumber" || key === "type";

          if(validKey) {
            if(key === "issuedDate") {
              const defaultValAsBCMSDate = BCMSDate.init().offset(defaultVal);
              const currValAsBCMSDate = BCMSDate.init().offset(currVal);
              const sameDay = defaultValAsBCMSDate.date.getFullYear() === currValAsBCMSDate.date.getFullYear()
              && defaultValAsBCMSDate.date.getMonth() === currValAsBCMSDate.date.getMonth()
              && defaultValAsBCMSDate.date.getDate() === currValAsBCMSDate.date.getDate();
              
              if(!sameDay) {
                canCreateCheck = true;
              }
            } else if(defaultVal !== currVal) {
              canCreateCheck = true;
            }
          }
        });

        if(canCreateCheck === true) {
          const merge = BCMSDefaultModel.mergeRecords(item.record, defaultCheckRecord);
          const updatedCheckForm = TASPositivePayCheckForm.init(merge);
          updatedCheckForm.mobileViewIsEditing = false;
          submittedChecks.push(updatedCheckForm.record);
          acc.push(
            PositivePayCheck.create({ ...updatedCheckForm.record, account: updatedCheckForm.account.id })
          );
        }

        return acc;
      }, []);

      if(submittedChecks.length === 0) {
        const defaultCheck = BCMSDefaultModel.positivePayCheck();
        const defaultCheckRecord = defaultCheck.record;
        const merge = BCMSDefaultModel.mergeRecords(defaultCheckForm.record, defaultCheckRecord);
        const updatedCheckForm = TASPositivePayCheckForm.init(merge);
        updatedCheckForm.mobileViewIsEditing = false;
        submittedChecks.push(updatedCheckForm.record);
        createdChecks.push(
          PositivePayCheck.create({ ...updatedCheckForm.record, account: updatedCheckForm.account.id })
        );
      }

      submittedChecks[submittedChecks.length - 1].mobileViewIsOpen = true;

      BCMSRecord.batch(createdChecks, context, Classes);
      var currData = { data: { checkForms: submittedChecks } };
      callback(currData);
    }
  });

  return (
    <PageContainer className="tas-positive-pay-add-check-page" state={ { data, ui } } controller={ controller }>
      <PageContent />
    </PageContainer>
  );
}

export const getDefaultData = () => {
  const accounts = Account.initAllBusinessAssetOnly().filter(item => item.type === "checking");
  const selectedAccount = accounts[0] ?? {};
  var defaultCheckForm = TASPositivePayCheckForm.init({ id: PositivePayCheck.nextID(), account: selectedAccount.id });
  const checkForms = [defaultCheckForm];

  return {
    accounts: accounts,
    selectedAccount: selectedAccount,
    checkForms: checkForms,
    defaultCheckForm: defaultCheckForm,
    isReviewSubmittedChecksEnabled: false,
    isSubmitButtonEnabled: true
  }
};

export const getDefaultUI = () => {
  return {
    accountSelect: { isActive: false },
    cancelModal: { isActive: false },
  };
};

function PageContent(props) {
  const { data, ui, onAddChecksClick, onSubmitClick } = useStateManager();
  const accounts = data.accounts;

  const cusomSelectItems = accounts.reduce((acc, item) => {
    acc.push(
      <CustomSelectItem key={ `${ item.id }`} value={ `${ item.id }`}>{ `${ item.nickname } ${ item.number }` }</CustomSelectItem>
    );
    return acc;
  }, []);

  const navigate = useRoute();
  const navigateCallback = state => {
    navigate("/tasPositivePaySubmittedChecks", { state: state });
  };

  return (
    <PageContentView>
         <WidgetContainer className="tas-positive-pay-add-check-widget">
            <WidgetView text="Add Checks">
              <div className="flex-container flex-horizontal account-select-section-container">
                <div className="set-width account-select-container">
                    <FormRowView text="Account">
                        <CustomSelect active={ ui.accountSelect.isActive } value={ data.selectedAccount.id }>
                            { cusomSelectItems }
                        </CustomSelect>
                    </FormRowView>
                </div>
                <div className="flex-width"></div>
              </div>
              { <TASPositivePayAddChecksDesktopView edit={ { editCheckForms: data.editCheckForms } }
              cancelModal={ ui.cancelModal } /> }
              { <TASPositivePayAddChecksMobileView edit={ { editCheckForms: data.editCheckForms } }
              cancelModal={ ui.cancelModal } /> }
              <div className="flex-container flex-horizontal links-container">
                <a className="set-width clickable" data-point={ `TAS_Positive_Pay_Add_Checks_Add_More_Checks_Button` }
                onClick={ onAddChecksClick }>Add More Checks</a>
              </div>
              <div className="flex-container flex-horizontal button-container">
                <div className="set-width secondary-button-container disabled">
                    <SecondaryButton text="Review Submitted Check(s)" data-point={ `TAS_Positive_Pay_Add_Checks_Review_Submitted_Checks_Button` }
                    onClick={ undefined } />
                </div>
                  <div className="set-width">
                    <PrimaryButton className="set-width" text="Submit" data-point={ `TAS_Positive_Pay_Add_Checks_Submit_Button` }
                    onClick={ onSubmitClick(navigateCallback) } />
                  </div>
              </div>
            </WidgetView>
        </WidgetContainer>
    </PageContentView>
  );
}